import {useAccessToken, useIsOrgAdmin, useIsOrgUnitPilot, useUserType} from "../../services/auth"
import {usePaging} from "../../hooks/usePaging"
import {ErrorDocument} from "../../resources/common"
import {exportUsers, findUsers} from "../../resources/user"
import {downloadFile} from "../../utilities/file"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import React, {useEffect, useMemo} from "react"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {useQueryState} from "use-location-state"
import {UnitPilotLimitsNotice} from "../UnitPilotLimitsNotice/UnitPilotLimitsNotice"
import SearchBox from "../SearchBox/SearchBox"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import Icon from "../Icon/Icon"
import {hasPermission} from "../../services/permission"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {Org} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AllowedUsersColumns} from "./Users.types"
import UsersTable from "./UsersTable"
import {useToasts} from "react-toast-notifications"

interface UsersProps {
	token?: string
	limit?: number
	includedColumns: AllowedUsersColumns[]
	enableTitle?: boolean
	fullHeight?: boolean
	enableOrgFilter?: boolean
	refreshToken?: number
}

export function CreateUserButton({showModal}: {showModal: () => void}) {
	const canCreateUser = hasPermission("user", "create")

	if (canCreateUser)
		return (
			<div>
				<button className="button button-create" onClick={() => showModal()}>
					<Icon icon="interface-add-1" size={12} /> Create
				</button>
			</div>
		)

	return null
}

export default function Users({
	limit = 25,
	includedColumns,
	enableTitle,
	fullHeight,
	enableOrgFilter,
	refreshToken,
}: UsersProps) {
	const accessToken = useAccessToken()
	const userType = useUserType()
	const isOrgAdmin = useIsOrgAdmin()
	const {addToast} = useToasts()
	const [_, refresh] = useRefreshToken()

	const [query, setQuery] = useQueryState("filter[query]", "")
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>("filter[orgs]", [])
	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) => findUsers(accessToken, offset, limit, filteredOrgs, query),
		(x) => x.data.length,
		[refreshToken, _, filteredOrgs.join(","), query]
	)

	const orgs =
		userType === "unit"
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Users", 500, onSearch)
	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	const filters = [...(enableOrgFilter ? [orgFilter] : [])]

	async function exportUsersHandler() {
		const response = await exportUsers(accessToken, query, filteredOrgs)

		response.match(
			(d) => downloadFile("Users.csv", d, "text/csv"),
			() => addToast("Something went wrong", {appearance: "warning"})
		)

		return true
	}

	return (
		<DataTableCard className={"users-card"}>
			<DataTableActionHeader
				searchBox={searchBox}
				enableSticky={fullHeight}
				filters={filters.length === 0 ? null : filters}
				title={enableTitle ? "Users" : null}
				exportFunc={isOrgAdmin ? exportUsersHandler : undefined}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: users}) => {
					return (
						<>
							<UsersTable
								users={users.data}
								meta={users.meta}
								hasResults={hasResults}
								hasPrev={hasPrev}
								hasNext={hasNext}
								prev={prev}
								next={next}
								refresh={refresh}
								isUsingPaging={true}
								include={users.included}
								fullHeight={fullHeight}
								includedColumns={includedColumns}
							/>
							{useIsOrgUnitPilot() && users.data.length >= 1 ? (
								<UnitPilotLimitsNotice text={"Maximum 1 user is available in Unit pilot"} />
							) : null}
						</>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
